import { useCallback } from 'react'
import { useStore } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useAppDispatch, type ReduxState } from '@/utils/typeHelpers'
import {
  launchQueued,
  openPanel,
  type HavenLaunch,
} from '@/features/adviser-chat/adviser-chat.slice'
import {
  selectActiveConversationId,
  selectActiveLaunchAction,
  selectActiveView,
} from '@/features/adviser-chat/adviser-chat.selectors'
import { isHavenPath } from '@/features/adviser-chat/lib/havenRoutes'
import {
  HAVEN_ENTRY_POINT,
  HAVEN_LAUNCH_ACTION,
  type HavenLaunchAction,
} from '@/features/adviser-chat/lib/types'
import { useOpenHavenFullScreen } from '@/features/adviser-chat/hooks/useOpenHavenFullScreen'

const LAUNCH_SURFACE: Record<HavenLaunchAction, 'panel' | 'fullScreen'> = {
  [HAVEN_LAUNCH_ACTION.openTickets]: 'panel',
  [HAVEN_LAUNCH_ACTION.quarterlyEstimates]: 'panel',
  [HAVEN_LAUNCH_ACTION.catchUpBookkeeping]: 'fullScreen',
}

/**
 * Starts a Haven launch where its action belongs: the panel or full screen. The Messages nav entry always
 * opens full screen. Full-screen Haven keeps every launch in place, and an action that is already the open
 * conversation is reopened, not restarted.
 * Reads the store at call time so the callback stays stable across Haven state changes.
 */
export const useLaunchHaven = () => {
  const dispatch = useAppDispatch()
  const store = useStore<ReduxState>()
  const { pathname } = useLocation()
  const openFullScreen = useOpenHavenFullScreen()

  return useCallback(
    (launch: HavenLaunch) => {
      const state = store.getState()
      const action =
        launch.context.type === 'action' ? launch.context.action : null
      const alreadyOpen =
        action !== null &&
        selectActiveView(state) === 'chat' &&
        selectActiveLaunchAction(state) === action
      if (!alreadyOpen) dispatch(launchQueued(launch))
      if (isHavenPath(pathname)) return
      const opensFullScreen =
        launch.entryPoint === HAVEN_ENTRY_POINT.sidebarMessages ||
        (action !== null && LAUNCH_SURFACE[action] === 'fullScreen')
      if (opensFullScreen) {
        openFullScreen(alreadyOpen ? selectActiveConversationId(state) : null)
      } else {
        dispatch(openPanel())
      }
    },
    [dispatch, openFullScreen, pathname, store]
  )
}
